import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAllUnreadNotifications, markAllAsRead } from 'api/notification';

// Query keys
export const notificationKeys = {
  all: ['notifications'],
  unread: ['notifications', 'unread'],
  unreadCount: ['notifications', 'unread', 'count'],
  paginated: (params) => ['notifications', 'paginated', params]
};

// Hook to get unread notifications
export const useUnreadNotifications = (options = {}) => {
  return useQuery({
    queryKey: notificationKeys.unread,
    queryFn: getAllUnreadNotifications,
    select: (data) => data.data?.data?.notifications || [],
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: 1000 * 30, // Refetch every 30 seconds
    refetchIntervalInBackground: false,
    ...options
  });
};

// Hook to count unread notifications using getAllUnreadNotifications
export const useUnreadNotificationCount = (options = {}) => {
  return useQuery({
    queryKey: notificationKeys.unreadCount,
    queryFn: getAllUnreadNotifications,
    select: (data) => {
      const notifications = data.data?.data?.notifications || [];
      // Count only unread notifications
      return notifications.filter((notification) => !notification.read).length;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: 1000 * 30, // Refetch every 30 seconds
    refetchIntervalInBackground: false,
    ...options
  });
};

// Hook to mark all notifications as read
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllAsRead,
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(notificationKeys.unread);
      await queryClient.cancelQueries(notificationKeys.unreadCount);
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousUnread) {
        queryClient.setQueryData(notificationKeys.unread, context.previousUnread);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(notificationKeys.unreadCount, context.previousCount);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries(notificationKeys.unread);
      queryClient.invalidateQueries(notificationKeys.unreadCount);
      queryClient.invalidateQueries(notificationKeys.all);
    }
  });
};
